import { useSyncExternalStore } from "react";
import { readCookie, writeCookie } from "./cookie";

/**
 * Site UI/document language, matching the options in profile settings. `id` is the code the backend
 * stores (`/api/user/set_language`); `locale` is the BCP 47 tag used for `<html lang>` and Intl formatting.
 */
export const SITE_LANGUAGES = [
  { id: "ru", flag: "ru", label: "Русский", locale: "ru-RU" },
  { id: "en", flag: "gb", label: "English", locale: "en-US" },
  { id: "ua", flag: "ua", label: "Українська", locale: "uk-UA" },
  { id: "bl", flag: "by", label: "Беларуская", locale: "be-BY" },
  { id: "de", flag: "de", label: "Deutsch", locale: "de-DE" },
  { id: "fr", flag: "fr", label: "Français", locale: "fr-FR" },
  { id: "cn", flag: "cn", label: "中文", locale: "zh-CN" }
] as const;

export type SiteLanguage = (typeof SITE_LANGUAGES)[number]["id"];
export const DEFAULT_LANGUAGE: SiteLanguage = "ru";
const COOKIE_NAME = "lang";
const CHANGE_EVENT = "monvravex:language-change";

export function isSiteLanguage(value: unknown): value is SiteLanguage {
  return typeof value === "string" && SITE_LANGUAGES.some((language) => language.id === value);
}

/** BCP 47 locale for Intl formatters and `<html lang>`. */
export function getLocale(language: SiteLanguage = readLanguage()) {
  return SITE_LANGUAGES.find((item) => item.id === language)?.locale ?? "ru-RU";
}

export function hasStoredLanguage() {
  return isSiteLanguage(readCookie(COOKIE_NAME));
}

export function readLanguage(): SiteLanguage {
  const raw = readCookie(COOKIE_NAME);
  return isSiteLanguage(raw) ? raw : DEFAULT_LANGUAGE;
}

/** Keeps `<html lang>` in step with the selected language (screen readers, hyphenation, browser translation). */
export function applyDocumentLanguage(language: SiteLanguage = readLanguage()) {
  document.documentElement.lang = getLocale(language).split("-")[0] ?? "ru";
}

export function setLanguage(language: SiteLanguage) {
  writeCookie(COOKIE_NAME, language);
  applyDocumentLanguage(language);
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** Adopts the account language on a device that has no explicit choice yet; an explicit device choice wins. */
export function adoptAccountLanguage(accountLanguage: unknown) {
  if (!hasStoredLanguage() && isSiteLanguage(accountLanguage) && accountLanguage !== readLanguage()) setLanguage(accountLanguage);
}

function subscribe(callback: () => void) {
  window.addEventListener(CHANGE_EVENT, callback);
  return () => window.removeEventListener(CHANGE_EVENT, callback);
}

export function useLanguage(): SiteLanguage {
  return useSyncExternalStore(subscribe, readLanguage, () => DEFAULT_LANGUAGE);
}
